import { AppDispatch } from "../redux/store";
import { Translation } from "../configs/translation";
import { ICustomerResponse } from "../typings";
import { ICartSession } from "../typings/interfaces/pos/cart-session";
import { IHalfPayment } from "../typings/interfaces/payment/halfPayment";
import { IBillResponse } from "../typings/interfaces/responses/new-bill-response";
import { IBillPaymentHistory } from "../typings/interfaces/responses/paymentResponse";
import { BillNoteResponse } from "../typings/interfaces/responses/bill-note-response";
import { ITrayPrintRequest } from "../typings/interfaces/print/ticket-print-request";
import { IBillItemIdListResponse } from "../typings/interfaces/responses/pickupResponses";
import { BillPickupStatus } from "../typings/enum/paymentTypes";
import { formatUSDate, formatUS12HourTime } from "./common-functions";
import { buildCustomizationLines, buildPaymentSummary, buildPrintReleases, releaseFields, customerStatusLetter, formatFullName, readyAfterWithService, formatGarmentColor, grossItemPrice, isDeferredPayment, paymentStatusLetter, printedBaseItemPrice, printedItemPrice, printedStrikePrice, processingBaseUnitPrice, remainingStoreCreditBalance, resolvePinTagAlias, toDisplayCase } from "./print-formatting";
import {
  getBillByIdAction,
  getBillPaymentHistoryAction,
  getBillNoteByIdAction,
  getBillItemIdListAction,
} from "../redux/actions/cartAction";
import { getCustomerByIdAction } from "../redux/actions/customerAction";
import { getAllColorPinTagsAction } from "../redux/actions/pintagAction";
import { receiptStoreFields } from "./store-header";

// Per-item pickup progress, keyed by cart item id (matches
// IBillItemIdListResponse.hashId - see PickupModal.tsx's prepareCartItem for
// the same lookup convention).
type ItemPickupProgress = { pickedQty: number; totalQty: number };

export type PrintablePayload = {
  translation: Translation;
  customer: ICustomerResponse;
  paymentData: IHalfPayment;
  session: ICartSession;
  discount?: number;
  dropOffDate: Date;
  pickUpDate: Date;
  service?: string;
  dropOffTime: Date;
  pickUpTime: Date;
  special: boolean;
  billNotes?: BillNoteResponse[];
  // Formatted display id (e.g. "DC26-2421") - distinct from the bill's raw
  // numeric `id`, which is what `paymentData.billId` actually holds.
  billId: string;
  pinTagAlias?: string;
  // Pickup progress per item (itemId -> pieces picked up / total pieces),
  // plus the bill-wide rollup used to decide the top-left "Ready By"/
  // "Picked Up"/"Due Next" state (Print Issues.pdf #4) and which items get
  // tagged "- Picked Up" in the item list (Print Issues.pdf #7c).
  itemPickupProgress: Map<string, ItemPickupProgress>;
  anyPickedUp: boolean;
  allPickedUp: boolean;
  // Best-effort "when was this picked up" - this app doesn't record a
  // distinct pickup timestamp, so this uses the most recent payment's
  // createdDate as a proxy (pickup happens alongside settling payment at
  // the counter in this POS's flow). Falls back to now() if there's no
  // payment history at all (shouldn't happen for a bill with anything
  // picked up, but keeps this from ever being undefined).
  pickedAt: Date;
};

export async function loadPrintableByInvoice(
  dispatch: AppDispatch,
  translation: Translation,
  invoiceNo: string
): Promise<PrintablePayload | null> {
  try {
    const billRes = await dispatch(getBillByIdAction({ billId: invoiceNo }));
    const bill = billRes.payload as IBillResponse;
    if (!bill) return null;

    let session: ICartSession;
    try {
      session = JSON.parse(bill.billString) as ICartSession;
    } catch {
      return null;
    }

    const custRes = await dispatch(
      getCustomerByIdAction(String(bill.customerId))
    );
    const customer = custRes.payload as ICustomerResponse;
    if (!customer) return null;

    const paymentRes = await dispatch(
      getBillPaymentHistoryAction({
        paymentType: null,
        billId: bill.id,
        customerId: bill.customerId,
        paymentClearStatus: null,
        startDate: null,
        endDate: null,
      })
    );
    const paymentHistory = paymentRes.payload as IBillPaymentHistory | null;

    const billNotesRes = await dispatch(getBillNoteByIdAction({ billId: bill.id }));
    const billNotes = billNotesRes.payload as BillNoteResponse[] | undefined;

    const colorRes = await dispatch(getAllColorPinTagsAction());
    const colorPayload = colorRes.payload as
      | { response?: { body?: Array<{ color: string; alias: string }> } }
      | undefined;
    const pinTagAlias = resolvePinTagAlias(
      session.pinTag?.color,
      colorPayload?.response?.body
    );

    const itemIdListRes = await dispatch(getBillItemIdListAction(bill.id));
    const itemIdListPayload = itemIdListRes.payload as
      | { billItemIdList?: IBillItemIdListResponse[] }
      | undefined;
    const billItemIdList = itemIdListPayload?.billItemIdList ?? [];

    const itemPickupProgress = new Map<string, ItemPickupProgress>();
    for (const item of session.cartItems ?? []) {
      const totalQty = Math.max(1, item.qty ?? 1);
      const apiItem = billItemIdList.find((bi) => bi.hashId === item.itemId);
      const pickedQty = Math.min(
        totalQty,
        Math.max(
          apiItem?.pickedQty ??
            (apiItem?.pickupStatus === BillPickupStatus.PICKED_UP ? totalQty : 0),
          0
        )
      );
      itemPickupProgress.set(item.itemId, { pickedQty, totalQty });
    }
    const progressValues = Array.from(itemPickupProgress.values());
    const anyPickedUp = progressValues.some((p) => p.pickedQty > 0);
    const allPickedUp =
      progressValues.length > 0 &&
      progressValues.every((p) => p.pickedQty >= p.totalQty);
    // Most recent payment as a proxy for "when this was picked up" - see
    // PrintablePayload.pickedAt's own comment for why.
    const sortedPayments = [...(paymentHistory?.payments ?? [])].sort(
      (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
    );
    const pickedAt = sortedPayments[0]?.createdDate
      ? new Date(sortedPayments[0].createdDate)
      : new Date();

    const parsedSession = session as any;
    const pickupDateStr = parsedSession?.pickupDetails?.dueDate;
    const pickUpDate = pickupDateStr ? new Date(pickupDateStr) : new Date();
    const dropOffDate = (bill as any).createdTime
      ? new Date((bill as any).createdTime)
      : new Date();
    // The bill's own flag is kept up to date by edits; the billString copy isn't
    const isSpecial =
      (bill as any).pickupDetails?.isSpecial ??
      parsedSession?.pickupDetails?.isSpecial ??
      false;
    const service =
      session.cartItems[0]?.serviceType?.categoryName ?? "Service";

    const paymentData: IHalfPayment = {
      billId: bill.id,
      fullpaymentAmount: bill.billAmount,
      halfPaymentAmount: (bill as any).paidAmount ?? bill.billAmount,
      customerId: bill.customerId,
      payments: paymentHistory?.payments ?? [],
    };

    return {
      translation,
      customer,
      paymentData,
      session,
      discount: bill.discountAmount,
      dropOffDate,
      pickUpDate,
      service,
      dropOffTime: dropOffDate,
      pickUpTime: pickUpDate,
      special: isSpecial,
      billNotes,
      billId: bill.billId,
      pinTagAlias,
      itemPickupProgress,
      anyPickedUp,
      allPickedUp,
      pickedAt,
    };
  } catch (e) {
    console.error("loadPrintableByInvoice failed:", e);
    return null;
  }
}

// Item display names ("Shirt – Blue – Cotton") for a CREATED event's frozen
// snapshot, in the same plain-name-list shape the timeline already renders
// for a pickup event (metadata.items/pickedUpItems/remainingItems) - used to
// show the bill's original item list on the "Order Created" card without
// needing a BE-resolved name list (see loadPrintableFromSnapshot's own
// comment on why that resolution stays FE-side).
export function describeSnapshotItems(originalBillSnapshot: string): string[] {
  try {
    const session = JSON.parse(originalBillSnapshot) as ICartSession;
    return (session.cartItems ?? []).map((item) => {
      const description =
        [
          item.garmentType?.name && toDisplayCase(item.garmentType.name),
          item.garmentColor?.map((c) => formatGarmentColor(String(c))).join(", "),
          item.materialType?.name && toDisplayCase(item.materialType.name),
          item.garmentStyle?.name && toDisplayCase(item.garmentStyle.name),
        ]
          .filter(Boolean)
          .join(" – ") || "Item";
      return item.qty > 1 ? `${description} x${item.qty}` : description;
    });
  } catch {
    return [];
  }
}

// Press Only / Clean Only details per snapshot item (same order as
// describeSnapshotItems), null for every other item: the regular price shown
// struck through and the reduced price charged.
export function describeSnapshotProcessing(
  originalBillSnapshot: string
): Array<{ label: string; regularPrice: number; price: number } | null> {
  try {
    const session = JSON.parse(originalBillSnapshot) as ICartSession;
    return (session.cartItems ?? []).map((item) => {
      const base = processingBaseUnitPrice(item);
      if (base === null) return null;
      return {
        label: item.pressOnly ? "Press Only" : "Clean Only",
        regularPrice: base * item.qty,
        price: item.unitPrice * item.qty,
      };
    });
  } catch {
    return [];
  }
}

// Builds a printable payload for a bill's "Order Created" timeline event,
// FROM the frozen snapshot captured on that event (metadata.originalBillSnapshot
// - see BillServiceImpl's CREATED event on the backend), instead of the
// bill's own live/current billString - so editing a bill after check-in
// never changes what this prints. Deliberately doesn't touch payment/pickup
// history: at the exact moment a bill is CREATED nothing has been paid or
// picked up yet (those are separate, later timeline events), so every item
// is unpicked and the full original amount is shown as owed.
export async function loadPrintableFromSnapshot(
  dispatch: AppDispatch,
  translation: Translation,
  createdEvent: {
    amount?: number | null;
    createdAt: string;
    metadata?: { originalBillSnapshot?: string | null } | null;
  },
  displayBillId: string,
  customerId: string | number
): Promise<PrintablePayload | null> {
  try {
    const snapshot = createdEvent.metadata?.originalBillSnapshot;
    if (!snapshot) return null;

    let session: ICartSession;
    try {
      session = JSON.parse(snapshot) as ICartSession;
    } catch {
      return null;
    }

    const custRes = await dispatch(getCustomerByIdAction(String(customerId)));
    const customer = custRes.payload as ICustomerResponse;
    if (!customer) return null;

    const colorRes = await dispatch(getAllColorPinTagsAction());
    const colorPayload = colorRes.payload as
      | { response?: { body?: Array<{ color: string; alias: string }> } }
      | undefined;
    const pinTagAlias = resolvePinTagAlias(
      session.pinTag?.color,
      colorPayload?.response?.body
    );

    // pickupDetails isn't on ICartSession's own type, but is present at
    // runtime on the parsed billString - same as the reprint builder above
    // relies on.
    const parsedSession = session as any;
    const pickupDateStr = parsedSession?.pickupDetails?.dueDate;
    const dropOffDate = new Date(createdEvent.createdAt);
    const pickUpDate = pickupDateStr ? new Date(pickupDateStr) : dropOffDate;
    const isSpecial = parsedSession?.pickupDetails?.isSpecial ?? false;
    const service = session.cartItems[0]?.serviceType?.categoryName ?? "Service";

    const itemPickupProgress = new Map<string, ItemPickupProgress>();
    for (const item of session.cartItems ?? []) {
      itemPickupProgress.set(item.itemId, {
        pickedQty: 0,
        totalQty: Math.max(1, item.qty ?? 1),
      });
    }

    // Gross (pre-discount) subtotal, summed straight from the snapshot's own
    // items via the same shared price helper every other builder uses.
    // `createdEvent.amount` is the bill's original NET (post-discount)
    // amount (BillServiceImpl sets it from `newBill.getBillAmount()` at
    // creation, before this InvoiceHistory row is ever touched again) - the
    // difference between the two is the original discount, without needing
    // a separate stored discount field.
    const grossSubtotal = (session.cartItems ?? []).reduce(
      (sum, item) => sum + grossItemPrice(item),
      0
    );
    const total = createdEvent.amount ?? grossSubtotal;
    const discount = Math.max(0, grossSubtotal - total);

    const paymentData: IHalfPayment = {
      billId: undefined,
      fullpaymentAmount: total,
      halfPaymentAmount: 0,
      customerId: customer.id,
      payments: [],
    };

    return {
      translation,
      customer,
      paymentData,
      session,
      discount,
      dropOffDate,
      pickUpDate,
      service,
      dropOffTime: dropOffDate,
      pickUpTime: pickUpDate,
      special: isSpecial,
      billNotes: [],
      billId: displayBillId,
      pinTagAlias,
      itemPickupProgress,
      anyPickedUp: false,
      allPickedUp: false,
      pickedAt: dropOffDate,
    };
  } catch (e) {
    console.error("loadPrintableFromSnapshot failed:", e);
    return null;
  }
}

export function buildTicketPrintRequest(
  payload: PrintablePayload,
  copyType: "receipt" | "storeCopy",
  // The bill itself carries no cashier name (IBillResponse only has a bare
  // cashierId), and a reprint isn't necessarily happening at check-in time,
  // so there's no "session cashier" to read off state the way check-in/
  // pickup do - the cashier performing THIS reprint is passed in explicitly
  // by the caller instead of silently printing blank, as it did before.
  cashier?: { name?: string; id?: string | number }
): ITrayPrintRequest {
  const paidBy = payload.paymentData.payments[0]?.paymentType ?? "";
  // `fullpaymentAmount` is the bill's NET amount (the backend stores
  // billAmount = items + charges - discount), so it already IS the printed
  // Total. Subtotal is the pre-discount figure: net + discount. Subtracting
  // the discount from it a second time double-counted it.
  const discount = payload.discount ?? 0;
  const total = payload.paymentData.fullpaymentAmount;
  const subtotal = total + discount;
  const paid = isDeferredPayment(paidBy) ? 0 : payload.paymentData.halfPaymentAmount;
  const paymentSummary = buildPaymentSummary({
    payments: payload.paymentData.payments,
    due: total,
  });

  // Top-left label (Print Issues.pdf #4): "Ready By" with the still-scheduled
  // date until anything's been picked up; once any piece has been picked up
  // it switches to "Picked Up" with the actual pickup time, and - only for a
  // partial pickup (some picked, some not) - an additional "Due Next" row
  // keeps showing the still-scheduled date for what remains.
  const pickupLabel = payload.anyPickedUp ? "Picked Up" : "Ready By";
  const pickupValue = payload.anyPickedUp ? payload.pickedAt : payload.pickUpDate;
  const isPartialPickup = payload.anyPickedUp && !payload.allPickedUp;

  const items = (payload.session.cartItems ?? []).map((item) => {
    const progress = payload.itemPickupProgress.get(item.itemId);
    // Only a FULLY picked-up line gets tagged - a partially-picked line
    // (some pieces of a multi-piece line already gone) isn't split into
    // separate "leaving"/"remaining" sub-lines the way the pickup
    // receipt does (PickupReceiptModal.tsx), so there's no single
    // accurate tag for it here; left untagged rather than guess.
    const isFullyPickedUp =
      !!progress && progress.pickedQty >= progress.totalQty;
    const baseDescription =
      [
        item.garmentType?.name && toDisplayCase(item.garmentType.name),
        item.garmentColor?.map((c) => formatGarmentColor(String(c))).join(", "),
        item.materialType?.name && toDisplayCase(item.materialType.name),
        item.garmentStyle?.name && toDisplayCase(item.garmentStyle.name),
      ]
        .filter(Boolean)
        .join(" – ") || "Item";
    return {
      qty: item.qty,
      description: isFullyPickedUp
        ? `${baseDescription} - Picked Up`
        : baseDescription,
      price: printedBaseItemPrice(item),
      strikePrice: printedStrikePrice(item),
      note: item.note ?? undefined,
      customizations: buildCustomizationLines(item),
    };
  });

  return {
    copyType,
    invoice: payload.billId,
    department: (payload.service ?? "DRY CLEANING").toUpperCase(),
    dropOff: `${formatUSDate(payload.dropOffDate)} · ${formatUS12HourTime(payload.dropOffTime)}`,
    pickupLabel,
    pickup: formatUSDate(pickupValue),
    pickupAfter: payload.anyPickedUp
      ? formatUS12HourTime(pickupValue)
      : readyAfterWithService(formatUS12HourTime(pickupValue), payload.special),
    dueNext: isPartialPickup ? formatUSDate(payload.pickUpDate) : undefined,
    dueNextAfter: isPartialPickup ? formatUS12HourTime(payload.pickUpTime) : undefined,
    customer: formatFullName(payload.customer),
    customerId: payload.customer.id.toString(),
    customerStatus: customerStatusLetter(payload.customer.customerTypePref),
    paymentStatus: paymentStatusLetter(payload.customer.customerPayAtPref),
    cashier: cashier?.name ?? "",
    cashierId: cashier?.id !== undefined ? String(cashier.id) : "",
    ...receiptStoreFields({
      name: payload.translation.companyName,
      address: payload.translation.companyAddress,
      phone: payload.translation.companyPhone,
    }),
    items,
    ...releaseFields(buildPrintReleases(payload.session.cartItems ?? [], items), copyType),
    subtotal,
    discount,
    total,
    notes: (payload.billNotes ?? []).map((n) => n.note).filter(Boolean),
    paidBy,
    paid,
    balance: Math.max(0, total - paid),
    // The "Order Created" snapshot reprint has no bill id and no payment
    // records (it reconstructs the original bill), so it keeps the legacy
    // Paid/Balance rows instead of claiming "No Payment Received".
    ...(payload.paymentData.billId !== undefined
      ? {
          payments: paymentSummary.payments,
          change: paymentSummary.change,
          balanceDue: paymentSummary.balanceDue,
          storeCreditBalance: remainingStoreCreditBalance(payload.paymentData.payments),
        }
      : {}),
    pinTagColor: payload.session.pinTag?.color ?? undefined,
    pinTagTags: payload.session.pinTag?.tags ?? [],
    pinTagAlias: payload.pinTagAlias,
  };
}
